import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { AdminWorkerDetail } from './AdminWorkerDetail.js'
import { WorkerSearchInput, WorkerSearchTable, isSearchable, useDebouncedValue } from '../components/WorkerSearchResults.js'
import type { ComponentProps } from 'react'
import type { RosterAdminApi } from '../adminApi.js'
import { LoadFailure } from '../../core/index.js'

/** The admin calls this screen makes itself, and what the screens it opens need. */
type AdminApi = RosterAdminApi & ComponentProps<typeof AdminWorkerDetail>['api']


/** The platform admin's worker search: every worker, across every client. Looking only: a worker is added by the client's operator. */
export function WorkerSearchPage({ api }: { api: AdminApi }) {
  const [text, setText] = useState('')
  const query = useDebouncedValue(text).trim()

  const searchQuery = useQuery({
    queryKey: ['admin', 'worker-search', query],
    queryFn: () => api.searchWorkers(query),
    enabled: isSearchable(query),
    placeholderData: keepPreviousData,
  })

  return (
    <div className="max-w-5xl mx-auto px-7 py-6">
      <WorkerSearchInput id="admin-worker-search" value={text} onChange={setText} placeholder="Search workers by the start of a name or ID, across all clients" />

      <div className="mt-4">
        {!isSearchable(text) ? (
          <div className="text-sm text-ink-mid">Type at least three letters: the start of a worker's first or last name, or of their ID. The search covers every onboarded client.</div>
        ) : searchQuery.isError ? (
          <LoadFailure message={searchQuery.error.message || 'The search failed.'} onRetry={() => void searchQuery.refetch()} retrying={searchQuery.isFetching} />
        ) : searchQuery.isPending || searchQuery.data?.query !== text.trim() ? (
          <div className="text-sm text-ink-mid">Searching…</div>
        ) : (
          <WorkerSearchTable
            key={searchQuery.data.query}
            found={searchQuery.data}
            scopeLabel="any client"
            renderDetail={(hit) => <AdminWorkerDetail api={api} clientId={hit.clientId} workerId={hit.workerId} />}
          />
        )}
      </div>
    </div>
  )
}
